// Central content for NJ Therapy Online — virtual therapy across all of New Jersey.
// Affiliated with Montville Therapy Group.

export const SITE_URL = "https://njtherapyonline.com";

export const BRAND = {
  name: "NJ Therapy Online",
  legalName: "NJ Therapy Online",
  affiliate: "Montville Therapy Group",
  // Where the "Affiliated with Montville Therapy Group" text links to.
  affiliateUrl: "http://njtherapyonline.com/",
  tagline: "Online therapy for all of New Jersey",
  logo: "/logo-nj-therapy-online.png",
  logoMark: "/logo-mark.webp",
  ogImage: "/og-image.jpg",
  // Person on a laptop at home in calm, neutral tones (virtual therapy).
  heroImage:
    "https://images.unsplash.com/photo-1507206130118-b5907f817163?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  secondaryImage:
    "https://images.unsplash.com/photo-1628645339131-0c39c7527856?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
};

// Inquiries are delivered (via Netlify Forms notifications) to info@njtherapyonline.com.
export const CONTACT = {
  phone: "(973) 358-2121",
  phoneHref: "tel:+19733582121",
  phoneE164: "+1-973-358-2121",
  email: "info@njtherapyonline.com",
  emailHref: "mailto:info@njtherapyonline.com",
  googleReview: "https://g.page/r/CdAqafm3AeoREAI/review",
  psychologyToday: "https://www.psychologytoday.com/us/therapists/reema-tajdar-pine-brook-nj/1315811",
  hours: [
    { day: "Monday – Friday", time: "10:00 AM – 9:00 PM" },
    { day: "Saturday – Sunday", time: "10:00 AM – 6:00 PM" },
  ],
};

export const IN_PERSON_NOTE = "In-person therapy also available at our Montville, NJ location.";
export const SESSION_LENGTH = "45–60 min";

export const INSURANCES = [
  "Horizon Blue Cross Blue Shield",
  "Aetna",
  "UnitedHealthcare",
  "Optum",
  "AmeriHealth",
  "And more",
];

export const NAV_LINKS = [
  { to: "/services", label: "Services" },
  { to: "/areas-we-serve", label: "Areas We Serve" },
  { to: "/about", label: "About" },
  { to: "/team", label: "Meet the Team" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

export const HOW_IT_WORKS = [
  {
    title: "Reach out",
    body: "Send a quick inquiry or call and tell us what you're looking for. It takes two minutes.",
  },
  {
    title: "Get matched",
    body: "We'll verify your insurance and match you with the right clinician.",
  },
  {
    title: "Start online sessions",
    body: "Meet your therapist over secure, HIPAA-compliant video from anywhere in New Jersey.",
  },
];

export const BENEFITS = [
  { title: "No commute", body: "Skip the Parkway and Turnpike traffic. Therapy fits into your day." },
  { title: "Same quality care", body: "Research shows online therapy works as well as in-person for anxiety, depression, and more." },
  { title: "Evening & weekend hours", body: "Weekday sessions until 9 PM, plus weekend availability." },
  { title: "Private & secure", body: "Encrypted, HIPAA-compliant video from any private space you choose." },
];

// Core services — rewritten for virtual delivery across New Jersey.
export const SERVICES = [
  {
    slug: "individual-therapy",
    title: "Individual Therapy",
    h1: "Online Individual Therapy in New Jersey",
    subtitle: "Adults 18+",
    icon: "User",
    metaTitle: "Online Individual Therapy NJ | Virtual Counseling for Adults",
    metaDescription:
      "Virtual one-on-one therapy for adults anywhere in New Jersey. Help with anxiety, depression, stress, trauma and life transitions. In-network with major insurance.",
    short: "One-on-one virtual sessions for anxiety, depression, stress, and life transitions.",
    intro:
      "Online individual therapy gives you a confidential space to work through what's weighing on you — without leaving home. Our licensed clinicians use evidence-based approaches like CBT and DBT to help you build coping skills and lasting change.",
    helps: [
      "Anxiety, worry, and panic",
      "Depression and low motivation",
      "Work burnout and life transitions",
      "Trauma and PTSD",
      "Grief and loss",
      "Relationship or family stress",
      "Low self-esteem and boundaries",
      "Feeling stuck or overwhelmed",
    ],
    approach: [
      "Cognitive Behavioral Therapy (CBT)",
      "Dialectical Behavior Therapy (DBT)",
      "Mindfulness-based interventions",
      "Solution-focused therapy",
      "Person-centered, trauma-informed care",
    ],
    virtualNote:
      "Sessions take place over secure video. Many adults find it easier to open up — and stay consistent — from their own space.",
    image:
      "https://images.unsplash.com/photo-1565625443865-2c41cdb647d2?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  },
  {
    slug: "teen-therapy",
    title: "Teen Therapy",
    h1: "Online Teen Therapy in New Jersey",
    subtitle: "Ages 12–17",
    icon: "Sparkles",
    metaTitle: "Teen Therapy NJ | Online Counseling for Teens & Adolescents",
    metaDescription:
      "Virtual teen therapy across New Jersey for anxiety, depression, school stress, social pressure and self-esteem. Teen-friendly, evidence-based online counseling.",
    short: "Teen-friendly online counseling for anxiety, mood, school stress, and confidence.",
    intro:
      "Teens are often most comfortable on a screen, making online therapy a natural fit. Our therapists give adolescents a safe, nonjudgmental space to process emotions, manage stress, and build confidence, with parents involved in the right ways.",
    helps: [
      "Teen anxiety and panic",
      "Depression and mood changes",
      "School stress and perfectionism",
      "Social anxiety, peer pressure, and bullying",
      "Family conflict",
      "Identity and self-esteem",
      "Emotional regulation",
      "Screen, sleep, and technology stress",
    ],
    approach: [
      "Cognitive Behavioral Therapy (CBT)",
      "DBT skills for teens",
      "Mindfulness and strength-based work",
      "Trauma-informed care",
      "Parent check-ins and collaboration",
    ],
    virtualNote:
      "No pickups or drop-offs. Teens join after school from a private room at home, so sessions don't compete with activities.",
    image:
      "https://images.unsplash.com/photo-1522198684868-88edd8463fc9?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  },
  {
    slug: "child-therapy",
    title: "Child Therapy",
    h1: "Online Child Therapy in New Jersey",
    subtitle: "Ages 4–11, with parent support",
    icon: "Smile",
    metaTitle: "Online Child Therapy NJ | Virtual Counseling for Kids",
    metaDescription:
      "Virtual child therapy in New Jersey for anxiety, big emotions, behavior and school stress. Interactive, play-based online sessions with parent guidance.",
    short: "Interactive, play-based sessions that help kids manage big feelings.",
    intro:
      "Our child therapists use interactive, play-based activities designed for the screen to help children express feelings, build coping skills, and feel confident at home and school. Parents are included for support between sessions.",
    helps: [
      "Anxiety, fears, and separation worries",
      "Tantrums, outbursts, and anger",
      "Behavior challenges at home or school",
      "Adjusting to changes",
      "Social skills and friendships",
      "Low self-esteem",
    ],
    approach: [
      "Play-based therapeutic activities",
      "CBT for kids",
      "Parent–child guidance",
      "Mindfulness and emotional awareness",
    ],
    virtualNote:
      "Younger children may have a parent nearby for part of the session. We'll recommend the best format for your child.",
    image:
      "https://images.unsplash.com/photo-1607211851821-8be3cd6146f0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  },
  {
    slug: "family-therapy",
    title: "Family Therapy",
    h1: "Online Family Therapy in New Jersey",
    subtitle: "Parents, kids & blended families",
    icon: "Users",
    metaTitle: "Online Family Therapy NJ | Virtual Family Counseling",
    metaDescription:
      "Virtual family therapy for New Jersey families. Improve communication, reduce conflict and navigate transitions together with strength-based online counseling.",
    short: "Improve communication and reduce conflict at home, together.",
    intro:
      "Online family therapy gets everyone in the same session — even when schedules, distance, or co-parenting make that hard. We help families communicate, solve problems, and restore balance.",
    helps: [
      "Parent–child conflict",
      "Communication breakdowns",
      "Blended-family transitions",
      "Co-parenting challenges",
      "Behavior issues affecting the home",
      "Loss or major life changes",
    ],
    approach: [
      "Family-systems interventions",
      "Communication and conflict-resolution skills",
      "Emotion-focused, trauma-informed strategies",
      "CBT and solution-focused techniques",
    ],
    virtualNote:
      "Family members can join from different homes — ideal for co-parents and families spread across NJ.",
    image:
      "https://images.unsplash.com/photo-1776944252339-1515c69ea096?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  },
  {
    slug: "couples-therapy",
    title: "Couples Therapy",
    h1: "Online Couples Therapy in New Jersey",
    subtitle: "Gottman-informed",
    icon: "HeartHandshake",
    metaTitle: "Online Couples Therapy NJ | Virtual Marriage Counseling",
    metaDescription:
      "Virtual couples counseling across New Jersey. Improve communication, rebuild trust and reconnect with Gottman-informed online therapy. Join together or from two locations.",
    short: "Rebuild trust and communication with Gottman-informed virtual sessions.",
    intro:
      "Online couples therapy helps partners communicate better, navigate conflict, and reconnect — from the same couch or two locations. Our clinicians use Gottman-informed, evidence-based tools to build healthier patterns.",
    helps: [
      "Communication issues and frequent arguments",
      "Trust issues or infidelity",
      "Emotional disconnection",
      "Parenting and family stress",
      "Finances, roles, and expectations",
      "Premarital counseling",
      "Intimacy concerns",
    ],
    approach: [
      "Gottman Method–informed therapy",
      "Emotion-Focused Therapy (EFT)",
      "Conflict-resolution skills",
      "Attachment-based work",
    ],
    virtualNote:
      "Busy schedules and childcare are the top reasons couples delay therapy. Online sessions remove both.",
    image:
      "https://images.unsplash.com/photo-1516349935484-52a0d805fdb1?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  },
  {
    slug: "emdr-therapy",
    title: "EMDR Therapy",
    h1: "Online EMDR Therapy in New Jersey",
    subtitle: "Trauma-focused care",
    icon: "Eye",
    metaTitle: "Online EMDR Therapy NJ | Virtual Trauma & PTSD Treatment",
    metaDescription:
      "Virtual EMDR therapy across New Jersey for trauma, PTSD, anxiety and painful memories. Evidence-based, trauma-informed online treatment.",
    short: "Evidence-based virtual EMDR to process trauma and painful memories.",
    intro:
      "EMDR is an evidence-based approach that helps the brain process painful memories so they feel less overwhelming. Delivered online with on-screen or self-tapping bilateral stimulation, it's a safe, effective option for many clients.",
    helps: [
      "Trauma and PTSD",
      "Anxiety, panic, and fears",
      "Negative self-beliefs",
      "Childhood emotional wounds",
      "Grief and loss",
      "Relationship trauma",
    ],
    approach: [
      "EMDR assessment and treatment planning",
      "Virtual bilateral stimulation",
      "Resourcing and stabilization",
      "Trauma-informed, attachment-focused care",
    ],
    virtualNote:
      "Processing from a familiar, safe space can make EMDR feel more grounding. We build stabilization skills first.",
    image:
      "https://images.unsplash.com/photo-1777052854676-b42a60893e56?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  },
  {
    slug: "art-therapy",
    title: "Art Therapy Services",
    h1: "Online Art Therapy Services in New Jersey",
    subtitle: "Creative therapeutic support",
    icon: "Palette",
    metaTitle: "Online Art Therapy Services NJ | Creative Virtual Counseling",
    metaDescription:
      "Creative, art-based virtual counseling for children, teens and adults in New Jersey. Reduce stress and express emotions — no artistic experience needed.",
    short: "Creative, art-based interventions for kids, teens, and adults.",
    intro:
      "Emotions aren't always easy to put into words. Art-based interventions offer a creative way to explore feelings and reduce stress — using simple supplies you have at home. No artistic experience needed.",
    helps: [
      "Anxiety and stress",
      "Emotional expression and self-esteem",
      "Coping skills after difficult experiences",
      "Emotional regulation",
      "Mindfulness and self-exploration",
    ],
    approach: [
      "Guided drawing and painting",
      "Creative expression exercises",
      "Mindfulness-based art activities",
      "Integration with talk therapy",
    ],
    virtualNote:
      "Art therapy interventions are provided within the scope of mental health counseling and are not a substitute for licensed expressive arts therapy or occupational therapy.",
    image:
      "https://images.unsplash.com/photo-1513364776144-60967b0f800f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  },
  {
    slug: "clinical-assessments",
    title: "Clinical Assessments",
    h1: "Online Clinical Assessments & Evaluations in New Jersey",
    subtitle: "Children, teens & adults",
    icon: "ClipboardCheck",
    metaTitle: "Online Mental Health Evaluations NJ | ESA, ADHD Screening & More",
    metaDescription:
      "Virtual clinical assessments in New Jersey: diagnostic evaluations, ADHD screenings, ESA evaluations, immigration hardship evaluations and treatment planning.",
    short: "Diagnostic evaluations, ADHD screenings, ESA and hardship evaluations.",
    intro:
      "Our licensed clinicians conduct thorough virtual evaluations to clarify diagnoses and build individualized treatment recommendations for children, teens, and adults.",
    helps: [
      "Diagnostic mental health evaluations",
      "Biopsychosocial assessments",
      "ADHD symptom screenings",
      "Emotional Support Animal (ESA) evaluations",
      "Immigration hardship evaluations",
      "Risk and safety assessments",
    ],
    approach: [
      "Clinical interviews",
      "Standardized screening measures",
      "Diagnostic impressions",
      "Individualized treatment planning",
    ],
    virtualNote:
      "Services are clinical counseling evaluations and do not include neuropsychological, forensic, or court-ordered psychological testing.",
    image:
      "https://images.unsplash.com/photo-1576091160550-2173dba999ef?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  },
];

// Condition-focused landing pages (high-intent SEO: "anxiety therapy NJ", etc.)
export const CONDITIONS = [
  {
    slug: "anxiety-therapy",
    title: "Anxiety",
    h1: "Online Anxiety Therapy in New Jersey",
    metaTitle: "Anxiety Therapy NJ | Online Anxiety Counseling & Treatment",
    metaDescription:
      "Virtual anxiety therapy anywhere in New Jersey. Evidence-based CBT and exposure therapy for worry, panic attacks, social anxiety and health anxiety. In-network.",
    short: "Worry, panic, social anxiety, and constant overthinking.",
    intro:
      "Anxiety can make everyday life exhausting — racing thoughts, tension, poor sleep, avoiding what you care about. Online anxiety therapy teaches proven tools to quiet the noise, practiced right where anxiety shows up: at home.",
    signs: [
      "Constant worry or racing thoughts",
      "Panic attacks or a pounding heart",
      "Avoiding situations, people, or places",
      "Trouble sleeping or relaxing",
      "Irritability and muscle tension",
      "Social or performance anxiety",
    ],
    approaches: ["Cognitive Behavioral Therapy (CBT)", "Exposure therapy", "DBT skills", "Mindfulness-based strategies"],
    relatedServices: ["individual-therapy", "teen-therapy", "child-therapy"],
  },
  {
    slug: "depression-therapy",
    title: "Depression",
    h1: "Online Depression Therapy in New Jersey",
    metaTitle: "Depression Therapy NJ | Online Depression Counseling",
    metaDescription:
      "Virtual depression therapy for adults and teens across New Jersey. Compassionate, evidence-based online counseling for low mood, burnout and loss of motivation.",
    short: "Low mood, low energy, and losing interest in things.",
    intro:
      "Depression can make even getting to an appointment feel impossible. Online therapy removes that barrier. Together we'll understand what's keeping you stuck and take steady steps toward feeling like yourself again.",
    signs: [
      "Persistent sadness or emptiness",
      "Loss of interest or motivation",
      "Fatigue and low energy",
      "Changes in sleep or appetite",
      "Feelings of guilt or worthlessness",
      "Withdrawing from friends and family",
    ],
    approaches: ["Cognitive Behavioral Therapy (CBT)", "Behavioral activation", "DBT skills", "Person-centered therapy"],
    relatedServices: ["individual-therapy", "teen-therapy", "family-therapy"],
  },
  {
    slug: "trauma-ptsd-therapy",
    title: "Trauma & PTSD",
    h1: "Online Trauma & PTSD Therapy in New Jersey",
    metaTitle: "Trauma & PTSD Therapy NJ | Online EMDR & Trauma Counseling",
    metaDescription:
      "Virtual trauma and PTSD therapy across New Jersey, including online EMDR and trauma-focused CBT. Heal at your own pace from a space where you feel safe.",
    short: "Painful memories, flashbacks, and feeling on edge.",
    intro:
      "Trauma can leave you feeling unsafe in your own body long after the event. Trauma-focused online therapy — including EMDR — helps you process what happened at a manageable pace, from a space that already feels safe.",
    signs: [
      "Intrusive memories or flashbacks",
      "Nightmares or trouble sleeping",
      "Feeling on edge or easily startled",
      "Avoiding reminders of the event",
      "Emotional numbness",
      "Shame, guilt, or self-blame",
    ],
    approaches: ["EMDR therapy", "Trauma-focused CBT", "Stabilization and grounding skills", "Person-centered care"],
    relatedServices: ["emdr-therapy", "individual-therapy", "teen-therapy"],
  },
  {
    slug: "ocd-therapy",
    title: "OCD",
    h1: "Online OCD Therapy in New Jersey",
    metaTitle: "OCD Therapy NJ | Online Exposure Therapy for OCD",
    metaDescription:
      "Virtual OCD therapy in New Jersey using evidence-based exposure therapy and CBT for intrusive thoughts and compulsions. Online sessions for teens and adults.",
    short: "Intrusive thoughts, compulsions, and reassurance-seeking.",
    intro:
      "OCD can trap you in exhausting cycles of intrusive thoughts and compulsions. Exposure-based therapy is the gold-standard treatment — and online, you can practice exposures where your OCD actually shows up.",
    signs: [
      "Unwanted, intrusive thoughts",
      "Repetitive checking, washing, or counting",
      "Constant reassurance-seeking",
      "Needing things to feel \"just right\"",
      "Avoidance to prevent anxiety",
      "Rituals that take up time each day",
    ],
    approaches: ["Exposure therapy", "Cognitive Behavioral Therapy (CBT)", "Mindfulness-based acceptance skills", "Psychoeducation"],
    relatedServices: ["individual-therapy", "teen-therapy", "clinical-assessments"],
  },
];

export const FAQ_CATEGORIES = [
  "Getting started",
  "Sessions & insurance",
  "Anxiety & stress",
  "Depression",
  "Teens & children",
  "Couples & ADHD",
  "About our practice",
];

export const FAQS = [
  {
    category: "Getting started",
    question: "Do you offer online therapy anywhere in New Jersey?",
    answer:
      "Yes. We provide virtual therapy in all 21 New Jersey counties, from Sussex to Cape May. You'll need to be located in New Jersey during each session. In-person therapy is also available at our Montville, NJ location.",
  },
  {
    category: "Getting started",
    question: "Is online therapy as effective as in-person therapy?",
    answer:
      "For most concerns — including anxiety, depression, stress, and relationship issues — research shows online therapy is as effective as in-person care. We'll help you decide if it's the right fit.",
  },
  {
    category: "Sessions & insurance",
    question: "Do you accept insurance?",
    answer:
      "Yes. We're in-network with Horizon Blue Cross Blue Shield, Aetna, UnitedHealthcare, Optum, AmeriHealth, and more. Private pay is also available, and we verify benefits before your first session.",
  },
  {
    category: "Sessions & insurance",
    question: "What do I need for a virtual session?",
    answer:
      "A phone, tablet, or computer with a camera, stable internet, and a private space to talk. We'll send a secure link before each session — no downloads required.",
  },
  {
    category: "Sessions & insurance",
    question: "Is online therapy private and secure?",
    answer:
      "Yes. Sessions use an encrypted, HIPAA-compliant video platform and follow professional and legal privacy standards.",
  },
  {
    category: "Teens & children",
    question: "Do you offer online therapy for teens and children?",
    answer:
      "Yes. We offer teen therapy for ages 12–17 and child therapy for ages 4–11, using age-appropriate approaches with parents involved as needed.",
  },
  {
    category: "Sessions & insurance",
    question: "How long are sessions and what are your hours?",
    answer:
      "Sessions are typically 45–60 minutes. Appointments are available Monday–Friday 10 AM–9 PM and Saturday–Sunday 10 AM–6 PM.",
  },
  {
    category: "About our practice",
    question: "Can healthcare providers or schools refer clients?",
    answer:
      "Absolutely. Professionals can refer clients through our contact form (choose \"Professional referral\") or by calling us.",
  },
  {
    category: "About our practice",
    question: "Are you connected to Montville Therapy Group?",
    answer:
      "Yes. NJ Therapy Online is affiliated with Montville Therapy Group, bringing the same licensed clinicians and evidence-based care statewide through telehealth.",
  },
  {
    category: "Getting started",
    question: "How do I find an online therapist in New Jersey?",
    answer:
      "NJ Therapy Online offers virtual therapy for adults, teens, children, and couples located in New Jersey. Share your needs, availability, and insurance information, and our team can help match you with a therapist.",
  },
  {
    category: "Sessions & insurance",
    question: "Does Aetna, Horizon Blue Cross Blue Shield, or UnitedHealthcare cover online therapy in New Jersey?",
    answer:
      "Coverage depends on your specific insurance plan and your therapist’s network participation. Contact NJ Therapy Online with your insurance information so we can check participation and benefits, including applicable copays, deductibles, or coinsurance.",
  },
  {
    category: "Anxiety & stress",
    question: "Do you offer online therapy for anxiety in New Jersey?",
    answer:
      "Yes. NJ Therapy Online offers anxiety therapy to help clients manage persistent worry, overthinking, stress, and avoidance. Your therapist works with you to understand your concerns and develop practical coping skills.",
  },
  {
    category: "Anxiety & stress",
    question: "What are signs of anxiety?",
    answer:
      "Signs of anxiety may include persistent worry, feeling restless or on edge, irritability, difficulty concentrating, trouble sleeping, muscle tension, a racing heart, or stomach discomfort. Some people also avoid situations that trigger worry or repeatedly seek reassurance. These symptoms can have other causes, so an assessment can help clarify what’s happening.",
  },
  {
    category: "Anxiety & stress",
    question: "How do I know if I need therapy for anxiety?",
    answer:
      "Consider therapy when worry, fear, or overthinking interferes with sleep, relationships, work, school, or everyday activities. You can also seek support before anxiety becomes overwhelming.",
  },
  {
    category: "Anxiety & stress",
    question: "Can therapy help with overthinking and stress?",
    answer:
      "Therapy can help you recognize patterns of rumination, challenge unhelpful thoughts, and develop ways to handle uncertainty. We offer online therapy in New Jersey for people feeling overwhelmed by daily demands, decisions, or ongoing stress.",
  },
  {
    category: "Depression",
    question: "Do you offer online therapy for depression in New Jersey?",
    answer:
      "Yes. NJ Therapy Online offers depression therapy for people experiencing persistent sadness, low motivation, loss of interest, or emotional disconnection. Your therapist helps you explore your experiences and work toward meaningful, manageable changes.",
  },
  {
    category: "Depression",
    question: "What are signs of depression?",
    answer:
      "Signs of depression may include persistent sadness, emptiness, irritability, loss of interest in activities, low energy, difficulty concentrating, changes in sleep or appetite, and feelings of worthlessness or hopelessness. If symptoms persist or interfere with daily life, consider seeking support. NJ Therapy Online offers online depression therapy in New Jersey to help you understand your experiences and develop coping strategies.",
  },
  {
    category: "Depression",
    question: "How do I know if I should seek therapy for depression?",
    answer:
      "Consider seeking support if low mood, hopelessness, withdrawal, or changes in sleep, appetite, or motivation persist or affect daily life. You do not need a confirmed depression diagnosis to contact a therapist.",
  },
  {
    category: "Depression",
    question: "Can I get help for anxiety and depression together?",
    answer:
      "Yes. Anxiety and depression can occur together. Your therapist can explore how worry, low mood, avoidance, and self-criticism affect you and develop a treatment plan that addresses your needs.",
  },
  {
    category: "Teens & children",
    question: "How do I find an online therapist for my teenager in New Jersey?",
    answer:
      "Contact NJ Therapy Online with your teen’s age, concerns, and availability. We can help match your teenager with a therapist who supports teens experiencing anxiety, mood concerns, school stress, or relationship difficulties.",
  },
  {
    category: "Teens & children",
    question: "What are signs my teenager may need counseling?",
    answer:
      "Changes in mood, withdrawal from friends, ongoing worry, school difficulties, or frequent conflict may be reasons to explore counseling. Therapy offers teens a supportive space to discuss concerns and develop coping skills.",
  },
  {
    category: "Teens & children",
    question: "Do you offer online therapy for children with anxiety?",
    answer:
      "Yes. We offer support for children experiencing anxiety, worries, and emotional challenges. We work with families to determine whether virtual therapy fits their child’s age, needs, and ability to participate.",
  },
  {
    category: "Couples & ADHD",
    question: "Can online couples therapy help with relationship problems?",
    answer:
      "Online couples therapy can help partners explore recurring conflict, communication difficulties, and trust concerns. NJ Therapy Online offers virtual couples counseling for partners located in New Jersey.",
  },
  {
    category: "Couples & ADHD",
    question: "Do you offer online therapy for adults with ADHD?",
    answer:
      "We support adults experiencing difficulties with attention, organization, time management, and overwhelm. Therapy can focus on practical routines, coping strategies, and self-criticism associated with ADHD.",
  },
  {
    category: "About our practice",
    question: "Is in-person therapy also available?",
    answer:
      "In-person therapy is also available through our affiliated practice, Montville Therapy Group, at our Montville, New Jersey location. Contact us for office appointment availability.",
  },
  {
    category: "Getting started",
    question: "How do I book an online therapy session in New Jersey?",
    answer:
      "Complete our appointment request form with your contact information, availability, and what you would like help with. Our team will follow up to help you find a therapist and schedule your first session.",
  },
];

export const THERAPISTS = [
  {
    "id": "reema-tajdar",
    "name": "Dr. Reema Tajdar",
    "credentials": "LPC, PhD, ACS",
    "title": "Founder & Licensed Professional Counselor",
    "subtitle": "ACS Approved Clinical Supervisor",
    "photo": "/team-reema-tajdar.webp",
    "intro": "Dr. Reema views therapy as a supportive space to slow down, reflect, and feel heard without judgment. She works collaboratively with clients to create meaningful, realistic steps toward change.",
    "bio": [
      "With over ten years of counseling experience, Dr. Reema has worked with clients from diverse backgrounds facing a wide range of challenges. She uses evidence-based approaches including CBT, DBT, Exposure Therapy, trauma-focused, and person-centered therapy, tailored to each client's goals.",
      "Dr. Reema holds an undergraduate degree in Psychology and a PhD in Counselor Education, and values psychoeducation as part of therapy. She believes healing happens at each person's own pace."
    ],
    "specialties": [
      "CBT",
      "DBT",
      "Exposure Therapy",
      "Trauma-Focused Therapy",
      "Person-Centered Therapy",
      "Psychoeducation"
    ]
  },
  {
    "id": "tiffany-flynn",
    "name": "Tiffany Flynn",
    "credentials": "LCSW",
    "title": "Licensed Clinical Social Worker",
    "photo": "/team-tiffany.webp",
    "intro": "Tiffany Flynn is a Licensed Clinical Social Worker with over 20 years of experience who earned her Master of Social Work from Fordham University. She works with children, teens, adults, and couples, including neurodivergent clients.",
    "bio": [
      "Her experience includes supporting clients with depression, anxiety, bipolar disorder, oppositional defiant disorder (ODD), obsessive-compulsive disorder (OCD), autism, and post-traumatic stress disorder (PTSD). Tiffany also has experience conducting Individualized Education Program (IEP) meetings and developing treatment plans for students to support their emotional and behavioral needs within the school setting.",
      "Tiffany’s approach is warm, collaborative, and nonjudgmental. She creates a supportive space where clients can explore their thoughts and feelings, build self-awareness, and feel comfortable being themselves. She helps clients recognize patterns, develop coping skills, and work toward meaningful changes that reflect their individual needs and goals."
    ],
    "specialties": [
      "Children & Teens",
      "Adults & Couples",
      "Neurodivergent Clients",
      "Anxiety & Depression",
      "OCD & PTSD",
      "IEP Support"
    ]
  },
  {
    "id": "ria-medasani",
    "name": "Ria Medasani",
    "credentials": "LAC",
    "title": "Licensed Associate Counselor",
    "photo": "/team-ria.webp",
    "intro": "Ria Medasani is a Licensed Associate Counselor with experience working with clients of all ages, with a particular focus on children and adolescents. She helps clients navigate challenges involving family, relationships, culture, and faith, while exploring personal values and building healthy boundaries.",
    "bio": [
      "Her approach incorporates cognitive restructuring, emotional regulation, and psychoeducation, along with creative tools such as art, music, and play. With younger clients, Ria focuses on reinforcing healthy behaviors, building self-esteem, and recognizing their strengths, unique personalities, and interests.",
      "Ria’s approach is empathetic, relatable, and transparent. She prioritizes building a strong therapeutic relationship and creating a supportive space where clients feel seen and heard. By meeting clients where they are, she helps them develop self-awareness and practical skills to navigate life’s challenges and support personal growth."
    ],
    "specialties": [
      "Children & Adolescents",
      "Family & Relationships",
      "Culture & Faith",
      "Emotional Regulation",
      "Art, Music & Play"
    ]
  },
  {
    "id": "alyssa-halwagy",
    "name": "Alyssa Halwagy",
    "credentials": "LAC",
    "title": "Licensed Associate Counselor",
    "photo": "/team-alyssa.webp",
    "intro": "Alyssa Halwagy works with individuals navigating anxiety, life transitions, stress, and emotional challenges. She supports clients who feel overwhelmed, stuck, or disconnected from themselves, their relationships, or their sense of purpose.",
    "bio": [
      "Alyssa has experience working in higher levels of care within mental health settings, supporting individuals with a wide range of needs. This background informs her adaptable approach, allowing her to meet each client where they are with care and clinical understanding.",
      "Alyssa’s approach is collaborative and compassionate. She helps clients slow down, make sense of their experiences, and build self-awareness. Together, they develop practical tools and insight to support meaningful change and move forward with greater clarity, confidence, and self-compassion."
    ],
    "specialties": [
      "Anxiety",
      "Life Transitions",
      "Stress",
      "Emotional Challenges",
      "Self-Compassion"
    ]
  },
  {
    "id": "kaitlin-malishkevich",
    "name": "Kaitlin Malishkevich",
    "credentials": "LPC",
    "title": "Licensed Professional Counselor",
    "photo": "/team-kaitlin.webp",
    "intro": "Kaitlin Malishkevich is a Licensed Professional Counselor with clinical experience supporting clients with anxiety, depression, mood disorders, obsessive-compulsive disorder (OCD), and trauma. She works with individuals who feel stuck, overwhelmed, or worn down by longstanding challenges or recent life events.",
    "bio": [
      "Her trauma-informed approach helps clients explore how emotional and behavioral patterns developed, how those patterns may have once protected them, and how they may now hold them back. Through this process, she supports clients in building self-awareness and moving toward meaningful change.",
      "Kaitlin creates a relaxed, supportive space through empathy, authenticity, and gentle humor. She brings professional training and lived experience to her work, helping clients feel understood and validated as they build confidence, self-compassion, and a stronger sense of worth."
    ],
    "specialties": [
      "Anxiety & Depression",
      "Mood Disorders",
      "OCD",
      "Trauma-Informed Care",
      "Self-Worth"
    ]
  },
  {
    "id": "shannon-campbell-rizzo",
    "name": "Shannon Campbell Rizzo",
    "credentials": "LAC",
    "title": "Licensed Associate Counselor",
    "subtitle": "EMDR Certified",
    "photo": "/team-shannon.webp",
    "intro": "Shannon Campbell Rizzo works with children, teens, adults, and couples navigating anxiety, trauma, stress, relationship challenges, self-esteem concerns, family issues, and life transitions.",
    "bio": [
      "She offers individualized, trauma-informed therapy incorporating cognitive behavioral therapy (CBT), dialectical behavior therapy (DBT), eye movement desensitization and reprocessing (EMDR), and play-based techniques for younger clients. Shannon helps clients understand their emotions and patterns, strengthen coping and communication skills, and process difficult experiences.",
      "Shannon’s style is warm, compassionate, collaborative, and authentic. She creates a supportive space where clients can feel comfortable, heard, and understood. By meeting clients where they are, she helps them explore what may be keeping them stuck, build on their strengths, and develop practical tools that support healing, growth, and connection."
    ],
    "specialties": [
      "EMDR",
      "CBT",
      "DBT",
      "Trauma",
      "Play-Based Therapy",
      "Couples & Families"
    ]
  },
  {
    "id": "giovanna-palmiotti",
    "name": "Giovanna Palmiotti",
    "credentials": "LSW",
    "title": "Licensed Social Worker",
    "photo": "/team-giovanna.webp",
    "intro": "Giovanna Palmiotti, LSW, works with teens and adults ages 13–65 navigating anxiety, depression, burnout, life transitions, relationship and peer conflicts, self-image concerns, and challenges with anger management.",
    "bio": [
      "She offers individualized support that helps clients explore their emotions, better understand their experiences, and develop practical tools for managing everyday challenges. Giovanna works collaboratively with clients to strengthen coping and communication skills and move toward their personal goals.",
      "Giovanna creates a safe, welcoming space, both in person and virtually, grounded in respect, autonomy, and confidentiality. She helps clients feel comfortable, heard, and supported as they build on their strengths and work toward meaningful change."
    ],
    "specialties": [
      "Teens & Adults",
      "Anxiety & Depression",
      "Burnout",
      "Life Transitions",
      "Self-Image",
      "Anger Management"
    ]
  }
];

export const INQUIRY_TYPES = [
  "Therapy for myself",
  "Therapy for my child or teen",
  "Couples or family therapy",
  "Professional referral",
];
